import React, { useCallback, useEffect } from 'react'
import { areGuideSettingsDialogShortcutsDisabled } from '@/lib/keyboard/dialog-shortcuts'
import { writeStoredEditorZoom } from '@/lib/zoom/editor-zoom-storage'
import { writeStoredSpreadIndex } from '@/lib/editor/editor-spread-storage'
import {
  commitZoomPercent,
  computeNextZoomIn,
  computeNextZoomOut,
  resolveFitToSpreadShellAction,
} from '@/lib/zoom/editor-zoom'
import {
  isEditorZoomModifierShortcut,
  tryResolveEditorZoomKeyboardAction,
} from '@/lib/zoom/editor-zoom-keyboard'
import { findSpreadIndexForPageNumber } from '@/api/hooks/use-spreads'
import { getDevLandingPageNumber } from '@/config/config'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomeHistory,
  HomeToolsAndSelection,
  HomeZoomAndSpread,
} from '../../types/home-groups'
import { isDevLandingPageOverrideConfigured } from '../../helpers/dev-landing-page'

/**
 * Zoom commands (in / out / fit / actual size / selection / percent), persisting zoom and spread
 * index and restoring the spread on load, the zoom screen-reader announcement, the undo / redo /
 * zoom keyboard shortcuts, and blocking the browser's own Ctrl+wheel / pinch page zoom.
 */
export function useHomeZoomCommands(
  deps: Pick<HomeFoundation, 'centerCanvasRef'> &
    Pick<
      HomeZoomAndSpread,
      | 'spreadsRaw'
      | 'zoom'
      | 'setZoom'
      | 'fitToSpreadViewportOk'
      | 'spreadIndex'
      | 'setSpreadIndex'
      | 'effectiveCanvasZoomRef'
      | 'effectiveEditorZoom'
      | 'effectiveEditorZoomRef'
      | 'canZoomIn'
      | 'canZoomOut'
      | 'flashZoomToolbarFromKeyboard'
      | 'isRestoringSpread'
      | 'spreadNavigationLocked'
      | 'orderedSpreads'
      | 'spreadId'
      | 'lastAnnouncedZoomPercentRef'
      | 'prevSpreadIdForZoomAnnounceRef'
      | 'recordZoomScreenReader'
    > &
    Pick<HomeDocumentLayout, 'hasDocumentForFit' | 'fitToSpreadDisabled'> &
    Pick<HomeToolsAndSelection, 'canZoomToSelection'> &
    Pick<HomeHistory, 'handleUndo' | 'handleRedo'>
) {
  const {
    centerCanvasRef,
    spreadsRaw,
    zoom,
    setZoom,
    fitToSpreadViewportOk,
    spreadIndex,
    setSpreadIndex,
    effectiveCanvasZoomRef,
    effectiveEditorZoom,
    effectiveEditorZoomRef,
    canZoomIn,
    canZoomOut,
    flashZoomToolbarFromKeyboard,
    isRestoringSpread,
    spreadNavigationLocked,
    orderedSpreads,
    spreadId,
    lastAnnouncedZoomPercentRef,
    prevSpreadIdForZoomAnnounceRef,
    recordZoomScreenReader,
    hasDocumentForFit,
    fitToSpreadDisabled,
    canZoomToSelection,
    handleUndo,
    handleRedo,
  } = deps

  const handleZoomIn = useCallback(() => {
    recordZoomScreenReader({ type: 'armToolbarIntent', direction: 'in' })

    if (centerCanvasRef.current?.zoomInFromToolbar()) return

    const next = computeNextZoomIn({
      zoom,
      effectiveCanvasZoom: effectiveCanvasZoomRef.current,
    })
    const currentEff = zoom === 0 ? effectiveCanvasZoomRef.current : zoom
    const currentPct = Math.round(currentEff * 100)
    const nextPct = Math.round(next * 100)

    if (nextPct === currentPct) {
      recordZoomScreenReader({ type: 'clearToolbarIntent' })

      return
    }

    recordZoomScreenReader({
      type: 'announceToolbarShellCommit',
      direction: 'in',
      nextPct,
    })

    setZoom(() => next)
  }, [zoom, recordZoomScreenReader, centerCanvasRef, effectiveCanvasZoomRef, setZoom])

  const handleZoomOut = useCallback(() => {
    recordZoomScreenReader({ type: 'armToolbarIntent', direction: 'out' })

    if (centerCanvasRef.current?.zoomOutFromToolbar()) return

    const next = computeNextZoomOut({
      zoom,
      effectiveCanvasZoom: effectiveCanvasZoomRef.current,
    })
    const currentEff = zoom === 0 ? effectiveCanvasZoomRef.current : zoom
    const currentPct = Math.round(currentEff * 100)
    const nextPct = Math.round(next * 100)

    if (nextPct === currentPct) {
      recordZoomScreenReader({ type: 'clearToolbarIntent' })

      return
    }

    recordZoomScreenReader({
      type: 'announceToolbarShellCommit',
      direction: 'out',
      nextPct,
    })

    setZoom(() => next)
  }, [zoom, recordZoomScreenReader, centerCanvasRef, effectiveCanvasZoomRef, setZoom])

  const handleZoomFit = useCallback(() => {
    recordZoomScreenReader({ type: 'clearToolbarIntent' })

    const action = resolveFitToSpreadShellAction(zoom, hasDocumentForFit, fitToSpreadViewportOk)

    if (action === 'noop') return

    if (action === 'refit') {
      centerCanvasRef.current?.refitFitSpread()

      return
    }

    setZoom(0)
  }, [
    hasDocumentForFit,
    fitToSpreadViewportOk,
    zoom,
    recordZoomScreenReader,
    centerCanvasRef,
    setZoom,
  ])

  const handleActualSize = useCallback(() => {
    recordZoomScreenReader({ type: 'clearToolbarIntent' })

    setZoom(1)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        centerCanvasRef.current?.centerSpreadForActualSize()
      })
    })
  }, [recordZoomScreenReader, centerCanvasRef, setZoom])

  const handleZoomToSelection = useCallback(() => {
    recordZoomScreenReader({ type: 'clearToolbarIntent' })

    centerCanvasRef.current?.zoomToFitSelection()
  }, [recordZoomScreenReader, centerCanvasRef])

  const handleZoomPercentCommit = useCallback(
    (percent: number) => {
      recordZoomScreenReader({ type: 'clearToolbarIntent' })

      const next = commitZoomPercent(percent)

      setZoom(next)
      centerCanvasRef.current?.centerViewportOnSelectionOrSpread()
    },
    [recordZoomScreenReader, centerCanvasRef, setZoom]
  )

  React.useEffect(() => {
    writeStoredEditorZoom(zoom)
  }, [zoom])

  React.useEffect(() => {
    // Embedded, the spread comes from the host; don't overwrite the standalone editor's last spread.
    if (spreadNavigationLocked) return
    writeStoredSpreadIndex(spreadIndex)
  }, [spreadIndex, spreadNavigationLocked])

  React.useEffect(() => {
    if (!isRestoringSpread.current || !spreadsRaw) return
    isRestoringSpread.current = false

    // Embedded, the active spread is the host's, found by id — there's no index to restore.
    if (!spreadNavigationLocked) {
      const devPageNumber = isDevLandingPageOverrideConfigured() ? getDevLandingPageNumber() : 0
      const devPageIndex =
        devPageNumber > 0 ? findSpreadIndexForPageNumber(spreadsRaw, devPageNumber) : null

      if (devPageIndex !== null) {
        setSpreadIndex(devPageIndex)
      } else {
        // Clamp in case the stored index is now out of bounds.
        setSpreadIndex((prev) => Math.min(prev, orderedSpreads.length - 1))
      }
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        centerCanvasRef.current?.centerSpreadForActualSize()
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadsRaw])

  /**
   * Announce effective  canvas zoom when it changes without going through toolbar in/out handlers
   * (e.g. canvas `zoomInFromToolbar`, percent field, fit). Toolbar +/- uses `recordZoomScreenReader`
   * for immediate shell commits; this effect seeds `lastAnnounced` and debounces the rest.
   * Short debounce limits noise during fit-to-page pinch. Use a real screen reader (NVDA, Narrator,
   * VoiceOver); browser “Reading mode” does not surface `aria-live` regions.
   */
  React.useEffect(() => {
    if (prevSpreadIdForZoomAnnounceRef.current !== spreadId) {
      prevSpreadIdForZoomAnnounceRef.current = spreadId
      recordZoomScreenReader({ type: 'resetAnnounceState' })
    }

    const pct = Math.round(effectiveEditorZoomRef.current * 100)

    if (lastAnnouncedZoomPercentRef.current === null) {
      recordZoomScreenReader({ type: 'seedLastAnnouncedSilently', pct })

      return
    }

    if (pct === lastAnnouncedZoomPercentRef.current) {
      return
    }

    const timer = window.setTimeout(() => {
      const latestPct = Math.round(effectiveEditorZoomRef.current * 100)

      recordZoomScreenReader({ type: 'announceDebouncedAfterZoomChange', nextPct: latestPct })
    }, 80)

    return () => window.clearTimeout(timer)
  }, [
    spreadId,
    effectiveEditorZoom,
    recordZoomScreenReader,
    effectiveEditorZoomRef,
    lastAnnouncedZoomPercentRef,
    prevSpreadIdForZoomAnnounceRef,
  ])

  // Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z / Ctrl/Cmd+Y redo; +/- or Ctrl/Cmd+/- zoom (same +10% / −10% as toolbar)
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (areGuideSettingsDialogShortcutsDisabled()) return

      const isUndoRedo =
        (e.ctrlKey || e.metaKey) &&
        !e.altKey &&
        (e.key === 'z' || e.key === 'Z' || e.key === 'y' || e.key === 'Y')
      const target = e.target as HTMLElement | null
      const isFormField =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.getAttribute('role') === 'textbox')

      // Ctrl/Cmd zoom keys type nothing, so they still zoom the spread from a text field;
      // bailing here would hand them to the browser's page zoom.
      const isModifierZoom = isEditorZoomModifierShortcut(e)
      const isTextBoxEditable = !!target?.isContentEditable

      if ((isFormField || isTextBoxEditable) && !isUndoRedo && !isModifierZoom) return

      const zoomAct = tryResolveEditorZoomKeyboardAction(e, {
        canZoomIn,
        canZoomOut,
        fitToSpreadDisabled,
        zoom,
      })

      if (zoomAct) {
        e.preventDefault()

        switch (zoomAct.kind) {
          case 'zoomIn':
            flashZoomToolbarFromKeyboard('in')
            handleZoomIn()
            break
          case 'zoomOut':
            flashZoomToolbarFromKeyboard('out')
            handleZoomOut()
            break
          case 'zoomInBlocked':
          case 'zoomOutBlocked':
            break
          case 'actualSize':
            recordZoomScreenReader({ type: 'clearToolbarIntent' })
            handleActualSize()
            break
          case 'fitToSpread':
            recordZoomScreenReader({ type: 'clearToolbarIntent' })
            if (zoom === 0) {
              centerCanvasRef.current?.refitFitSpread()
            } else {
              setZoom(0)
            }

            break

          case 'fitWidth':
            recordZoomScreenReader({ type: 'clearToolbarIntent' })
            centerCanvasRef.current?.fitWidth()
            break
          case 'zoomToSelectionShift':
            recordZoomScreenReader({ type: 'clearToolbarIntent' })
            centerCanvasRef.current?.zoomToFitSelection()
            break
          case 'zoomToSelectionCtrl':
            recordZoomScreenReader({ type: 'clearToolbarIntent' })
            if (canZoomToSelection) {
              centerCanvasRef.current?.zoomToFitSelection()
            }

            break
        }

        return
      }

      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        // `e.key` reflects the actual character produced, so the physical Z key comes through as
        // "Z" (uppercase) whenever Shift is held — comparing only against lowercase "z" below (as
        // this used to) means Ctrl/Cmd+Shift+Z's redo branch, `e.key === 'z' && e.shiftKey`, could
        // never match a real key press; redo only ever fired via the "y" fallback. Matches `isZ`
        // against `isUndoRedo`'s own case-insensitive check above.
        const isZ = e.key === 'z' || e.key === 'Z'
        const isY = e.key === 'y' || e.key === 'Y'

        if (isZ && !e.shiftKey) {
          e.preventDefault()
          handleUndo()
        } else if ((isZ && e.shiftKey) || isY) {
          e.preventDefault()
          handleRedo()
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    canZoomIn,
    canZoomOut,
    canZoomToSelection,
    fitToSpreadDisabled,
    handleActualSize,
    handleUndo,
    handleRedo,
    flashZoomToolbarFromKeyboard,
    handleZoomIn,
    handleZoomOut,
    recordZoomScreenReader,
    zoom,
    centerCanvasRef,
    setZoom,
  ])

  /**
   * Ctrl/Cmd+wheel and trackpad pinch (reported as wheel + ctrlKey, same convention
   * `center-canvas/hooks/use-ctrl-wheel-zoom.ts`'s pasteboard zoom handler relies on) must never trigger the
   * browser's native page zoom, no matter where the pointer is — only the pasteboard's own
   * wheel handler should respond to this gesture. Without this, the gesture only zooms the
   * pasteboard while the pointer is over the canvas; over the navbar or side panels it falls
   * through to the browser's default zoom instead.
   */
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
      }
    }

    window.addEventListener('wheel', onWheel, { passive: false })
    return () => window.removeEventListener('wheel', onWheel)
  }, [])

  return {
    handleZoomIn,
    handleZoomOut,
    handleZoomFit,
    handleActualSize,
    handleZoomToSelection,
    handleZoomPercentCommit,
  }
}
